import { useState, type FunctionComponent } from 'react'
import {
  PaginatedGrid,
  Badge,
  notify,
  NotificationLevel,
  Text,
} from '@retailer-platform/shared-components'
import { ApprovedIcon, TimeIcon } from '@instacart/ids-core'
import styled from '@emotion/styled'
import { LoadingPage } from '@retailer-platform/dashboard/gin-and-tonic'
import { Link } from '@retailer-platform/shared-components/src/tds'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { useListStepRunsQuery } from '../../../../api'
import { ResultDownloadButton } from './ResultDownloadButton'

type Props = {
  stepId: string
}

const NoResultsText = styled(Text)({
  textAlign: 'center',
  marginTop: '20px',
})

const StatusText = styled.div({
  textOverflow: 'ellipsis',
  textAlign: 'center',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  textWrap: 'auto',
})

export const ResultsTab: FunctionComponent<Props> = ({ stepId }) => {
  const i18n = useDomainMessages({
    id: 'catalogAdminDomain.attributeManagement.runResults.table.headers.id',
    requester: 'catalogAdminDomain.attributeManagement.runResults.table.headers.requester',
    version: 'catalogAdminDomain.attributeManagement.runResults.table.headers.version',
    completed: 'catalogAdminDomain.attributeManagement.runResults.table.headers.completed',
    productCount: 'catalogAdminDomain.attributeManagement.runResults.table.headers.productCount',
    downloadCSV: 'catalogAdminDomain.attributeManagement.runResults.table.headers.downloadCSV',
    startTime: 'catalogAdminDomain.attributeManagement.runResults.table.headers.startTime',
  })
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const { data, loading } = useListStepRunsQuery({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: page.toString() + '_' + itemsPerPage.toString(),
      stepId: stepId,
      page: page.toString(),
      pageSize: itemsPerPage.toString(),
    },
    onError: e => {
      notify({
        contents: `Error fetching step runs: ${e.message}`,
        level: NotificationLevel.Error,
      })
    },
  })
  return (
    <>
      {loading ? (
        <LoadingPage css={{ position: 'unset' }} />
      ) : !data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepRuns?.stepRuns?.length ? (
        <NoResultsText size="large" weight="semibold">
          No results to show.
        </NoResultsText>
      ) : (
        <PaginatedGrid
          columns={[
            {
              id: 'id',
              Cell: ({ row }) => (
                <>
                  <Link target="_blank" rel="noopener noreferrer" href={row.original.dashboardUrl}>
                    {row.original.id}
                  </Link>
                </>
              ),
              Header: i18n.id,
              width: 50,
            },
            {
              id: 'requester',
              Cell: ({ row }) => <>{row.original.requester}</>,
              Header: i18n.requester,
              width: 220,
            },
            {
              id: 'version',
              Cell: ({ row }) => <>{row.original.version}</>,
              Header: i18n.version,
              width: 75,
            },
            {
              id: 'stateMessage',
              Cell: ({ row }) => (
                <StatusText title={row.original.stateMessage || 'N/A'}>
                  {row.original.stateMessage || 'N/A'}
                </StatusText>
              ),
              Header: 'Message',
              width: 220,
            },
            {
              id: 'startTime',
              Cell: ({ row }) => <>{new Date(row.original.createdAt).toLocaleString('en-US')}</>,
              Header: i18n.startTime,
              width: 150,
            },
            {
              id: 'status',
              Cell: ({ row }) => (
                <>
                  {row.original.state === 'completed' && (
                    <Badge color={'green'}>
                      <ApprovedIcon size={28} color="systemSuccessRegular" />
                    </Badge>
                  )}
                  {row.original.state === 'in_progress' && (
                    <Badge color={'blue'}>
                      <TimeIcon size={28} />
                    </Badge>
                  )}
                  {row.original.state !== 'in_progress' && row.original.state !== 'completed' && (
                    <Badge color={'red'}>
                      <TimeIcon size={28} color="systemDetrimentalRegular" />
                    </Badge>
                  )}
                </>
              ),
              Header: i18n.completed,
              width: 60,
            },
            {
              id: 'productCount',
              Cell: ({ row }) => <>{row.original.productCount}</>,
              Header: i18n.productCount,
              width: 120,
            },
            {
              id: 'download',
              Cell: ({ row }) => <>{<ResultDownloadButton stepRunId={row.original.id || ''} />}</>,
              Header: i18n.downloadCSV,
              width: 100,
            },
          ]}
          data={data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepRuns?.stepRuns || []}
          onPageChange={setPage}
          page={page}
          showItemsPerPageSelector
          totalPages={parseInt(
            data?.instacart_catalog_catflow_v1_CatflowAPI_ListStepRuns?.pageCount || '1'
          )}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          width={'100%'}
        />
      )}
    </>
  )
}
